import React, {useEffect, useState} from "react";
import {useAtomValue} from "jotai";
import {MbmProvider} from "@amzn/react-arb-tools";
import {LocalizationContextBuilder} from "@amzn/arb-tools";
import {Navigate, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import {
  Alert,
  AppLayout,
  Box,
  ContentLayout,
  Header as AWSHeader,
  SpaceBetween,
  StatusIndicator
} from "@amzn/awsui-components-console";
import {DEFAULT_LOCALE} from "../../constants";
import Header from "../molecules/Header";
import Footer from "../molecules/Footer";
import WelcomeSection from "../molecules/WelcomeSection";
import ProfileCreation from "./ProfileCreation";
import ProfileDetail from "./ProfileDetail";
import ProfilesSection from "./ProfilesSection";
import JobsPage from "./JobsPage";
import JobCreateEdit from "./JobCreateEdit";
import JobHistory from "./JobHistory";
import JobHistoryOverview from "./JobHistoryOverview";
import ErrorPage from "./ErrorPage";
import {getUserAtom, userDisplayNameAtom} from "../../contexts/userContext";
import {signOut} from "../../utils/auth";
import {configureNProgress} from "../../config/nprogress";
import "../../styles/nprogress-custom.scss";
import arbManifest from "translations/arbManifest";
import {resolveBundleUrlWithFallbacks} from "../../helpers";

const mbmOptions = {
  arbManifest,
  defaultLocalizationContext: new LocalizationContextBuilder()
  .withLocale(DEFAULT_LOCALE)
  .withDefaultLocale(DEFAULT_LOCALE)
  .build(),
  resolveBundleUrl: (
      resourcePackPath: string | undefined,
      locale: string,
      bundle: string
  ) => resolveBundleUrlWithFallbacks(resourcePackPath, locale, bundle),
};

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profiles, setProfiles] = useState<any[]>([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [apiTestStatus, setApiTestStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [apiTestMessage, setApiTestMessage] = useState<string>('');

  // Get authenticated user from Jotai state
  const authenticatedUser = useAtomValue(getUserAtom);
  const userDisplayName = useAtomValue(userDisplayNameAtom);

  // Initialize NProgress on mount
  useEffect(() => {
    configureNProgress();
  }, []);


  const handleUserMenuClick = async (event: any) => {
    const {id} = event.detail;

    switch (id) {
      case "signout":
        try {
          await signOut();
          // The Authenticate component will handle showing the sign-in page
          window.location.reload(); // Reload to clear app state
        } catch (error) {
          setSuccessMessage("Error signing out. Please try again.");
        }
        break;
      default:
        console.log("Unknown menu item:", id);
        break;
    }
  };

  const handleCreateProfile = () => {
    navigate("/create-profile");
  };

  const handleProfileCreationSubmit = (profileData: any) => {
    // Profile has been successfully created via API
    // The profileData contains the response from CreateProfile API

    // Extract the profile ID from the response
    const profileId = profileData.profileId || profileData.id;
    const profileName = profileData.profileName || profileData.name;

    // Create a profile object for the local state
    const newProfile = {
      id: profileId,
      name: profileName,
      description: profileData.description,
      createdBy: profileData.createdBy || authenticatedUser?.username || "Unknown",
      lastUpdated: profileData.createdAt || new Date().toISOString(),
      status: "active" as const,
      ldapGroup: profileData.ldap || "US-ACCOUNT-GL",
    };

    // Update local state
    setProfiles([...profiles, newProfile]);

    // Set success message
    setSuccessMessage(`Profile "${profileName}" created successfully! Redirecting...`);

    // Navigate to the newly created profile page
    setTimeout(() => {
      navigate(`/profile/${profileId}`);
      // Clear success message after navigation
      setTimeout(() => setSuccessMessage(""), 1000);
    }, 1500); // Give user time to see the success message
  };

  const handleProfileCreationCancel = () => {
    navigate("/");
  };

  const handleProfileSelect = (profileId: string) => {
    navigate(`/profile/${profileId}`);
  };

  const HomePage = () => (
      <ContentLayout
          defaultPadding={false}
          headerVariant="high-contrast"
          header={<WelcomeSection/>}
      >
        <Box padding={{horizontal: "xl", vertical: "l"}}>
          <SpaceBetween size="l" direction="vertical">
            {/* API Connection Test Status */}
            {apiTestStatus !== 'idle' && (
                <Alert
                    type={apiTestStatus === 'loading' ? 'info' : apiTestStatus === 'success' ? 'success' : 'error'}
                    dismissible={apiTestStatus !== 'loading'}
                    onDismiss={() => {
                      setApiTestStatus('idle');
                      setApiTestMessage('');
                    }}
                    header={apiTestStatus === 'loading' ? 'Testing API Connection' : apiTestStatus === 'success' ? 'API Connection Successful' : 'API Connection Failed'}
                >
                  {apiTestStatus === 'loading' ? (
                      <StatusIndicator type="loading">{apiTestMessage}</StatusIndicator>
                  ) : (
                      apiTestMessage
                  )}
                </Alert>
            )}

            {successMessage && (
                <Alert
                    type="success"
                    dismissible
                    onDismiss={() => setSuccessMessage("")}
                >
                  {successMessage}
                </Alert>
            )}

            <ProfilesSection
                profiles={profiles}
                loading={false}
                onCreateProfile={handleCreateProfile}
                onProfileSelect={handleProfileSelect}
            />
          </SpaceBetween>
        </Box>
      </ContentLayout>
  );

  return (
      <MbmProvider {...mbmOptions}>
        <div className="addapt-app">
          <Header
              userName={authenticatedUser?.username || "Guest"}
              displayName={userDisplayName}
              applicationName="ADDAPT"
              onSignOut={async () => {
                try {
                  await signOut();
                  window.location.reload();
                } catch (error) {
                  console.error("Error signing out:", error);
                }
              }}
              onUserMenuClick={handleUserMenuClick}
          />
          <AppLayout
              navigation={null}
              navigationHide={true}
              toolsHide={true}
              content={
                <Routes>
                  <Route path="/" element={<HomePage/>}/>
                  {/* Redirect /profiles to home */}
                  <Route path="/profiles" element={<Navigate to="/" replace/>}/>
                  <Route path="/create-profile" element={
                    <ProfileCreation
                        onCancel={handleProfileCreationCancel}
                        onSubmit={handleProfileCreationSubmit}
                    />
                  }/>
                  <Route path="/profile/:id" element={<ProfileDetail/>}/>
                  <Route path="/profile/:id/jobs" element={<JobsPage/>}/>
                  <Route path="/profile/:id/job/:jobId" element={<JobCreateEdit/>}/>
                  <Route path="/profile/:id/job/:jobId/history" element={<JobHistory/>}/>
                  <Route path="/profile/:id/job/:jobId/run/:runId" element={<JobHistoryOverview/>}/>
                  <Route path="/old-run-view" element={
                    <ContentLayout
                        header={<AWSHeader variant="h1">Job Run Details</AWSHeader>}
                    >
                      <Box padding={{horizontal: "xl", vertical: "l"}}>
                        <Alert type="info">Job run details view coming soon...</Alert>
                      </Box>
                    </ContentLayout>
                  }/>
                  <Route path="/profile/:id/create-job" element={<JobCreateEdit/>}/>
                  <Route path="/error" element={<ErrorPage/>}/>
                  {/* Redirect all unknown routes to home */}
                  <Route path="*" element={<Navigate to="/" replace/>}/>
                </Routes>
              }
              headerSelector="#header"
              footerSelector="#footer"
              maxContentWidth={Number.MAX_VALUE}
              disableContentPaddings={true}
              contentType="default"
              ariaLabels={{
                navigation: "Navigation",
                navigationClose: "Close navigation",
                navigationToggle: "Open navigation",
                notifications: "Notifications",
                tools: "Help panel",
                toolsClose: "Close help panel",
                toolsToggle: "Open help panel"
              }}
          />
          <Footer/>
        </div>
      </MbmProvider>
  );
};

export default App;
